import { UtmParamKey } from "@outschool/attribution";
import { Box, Typography } from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import { useMachineTranslation, useTranslation } from "@outschool/localization";
import { addParamsToUrl, removeParamsFromUrl } from "@outschool/routes";
import { IntegrationCategory, useTrackEvent } from "@outschool/ui-analytics";
import { AuthTrigger, useGetAuthReason, useSession } from "@outschool/ui-auth";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import {
  Container,
  Image,
  Loading,
  Modal
} from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import gql from "graphql-tag";
import _ from "lodash";
import React from "react";
import { Query } from "react-apollo";
import { useHistory, useLocation, useParams } from "react-router";

import PerfectGiftForKids from "../../images/giftcard/PerfectGiftForKids.png";
import { MIN_PRICE, totalClassesString } from "../../shared/Activity";
import { ActivityFragment } from "../../shared/client/fragments/ActivityFragments";
import { LeaderFragment } from "../../shared/client/fragments/LeaderFragment";
import { SignupModalQuery } from "../../shared/generated/graphql";
import * as helpers from "../../shared/helpers";
import * as Routes from "../../shared/Routes";
import ActionType from "../actions/ActionType";
import { ParentFragment } from "../queries/ParentFragments";
import { useAppState } from "../stores/AppStateProvider";
import SignupForm from "./SignupForm";
import { SignupReferralModalContents } from "./SignupReferralModal";

const removableSignUpParams = ["signup", "authTrigger"];
const removablePostLoginParams = removableSignUpParams.concat(
  Object.values(UtmParamKey)
);

const signupModalQuery = gql`
  query SignupModal(
    $activityUid: ID
    $activitySlugId: String
    $skipActivity: Boolean!
    $referrerSlugId: String
    $skipReferrer: Boolean!
    $leaderUid: ID
    $leaderLink: String
    $skipLeader: Boolean!
  ) {
    activity(uid: $activityUid, slugId: $activitySlugId)
      @skip(if: $skipActivity) {
      ...ActivityFragment
    }
    leader: publicProfileNew(uid: $leaderUid, leader_link: $leaderLink)
      @skip(if: $skipLeader) {
      uid
      leader {
        name
      }
    }
    referrer: publicProfileNew(slug_id: $referrerSlugId)
      @skip(if: $skipReferrer) {
      uid
      parent {
        ...ParentFragment
      }
      leader {
        ...LeaderFragment
      }
    }
  }
  ${ActivityFragment}
  ${ParentFragment}
  ${LeaderFragment}
`;

export default function SignupModal() {
  const appState = useAppState();
  const history = useHistory();
  const { t } = useTranslation("client\\components\\SignupModal");
  const { pathname, query, search } = useLocation();
  const { activitySlugOrUid, uidOrLink } = useParams<{
    activitySlugOrUid?: string;
    uidOrLink?: string;
  }>();
  const { isLoggedIn } = useSession();
  const [showModal, setShowModal] = React.useState(
    Boolean(query?.signup) && !isLoggedIn
  );
  const [signup, setSignup] = React.useState(Boolean(query?.signup));
  const trackEvent = useTrackEvent();

  React.useEffect(() => {
    const signupInQuery = Boolean(query?.signup);
    if (signupInQuery != signup) {
      setSignup(signupInQuery);
    }
    const nextShowModal = signupInQuery && !isLoggedIn;
    if (showModal != nextShowModal) {
      setShowModal(nextShowModal);
    }
  }, [query?.signup, isLoggedIn, showModal, signup]);

  const pushClosedStateToRouter = React.useCallback(() => {
    let pathnameToRoute = pathname;
    if (query && query.authTrigger === AuthTrigger.ENROLL) {
      // The "enroll" modal is sometimes triggered inside of another modal, or
      // when the user clicks on "show details". Both of these triggers another
      // set of params to the path. If we don't do this here, it will cause a
      // loop in the modal close logic.
      pathnameToRoute = pathnameToRoute.replace("enroll", "");
    }
    // Try to use this instead of manually replacing the enroll part
    // goBackToActivityPage
    history.replace(
      addParamsToUrl(pathnameToRoute, _.omit(query, removableSignUpParams)),
      { noScroll: true }
    );
  }, [pathname, query, history]);

  React.useEffect(() => {
    if (!showModal && signup) {
      pushClosedStateToRouter();
    } else if (showModal) {
      setTimeout(() => {
        appState.appDispatcher.dispatch(ActionType.Webapp.ENTER_LOGIN_FLOW, {
          postLoginPath: removeParamsFromUrl(
            pathname + search,
            removablePostLoginParams
          )
        }),
          1000;
      });
      trackEvent("display-signup-modal");
    }
  }, [
    appState.appDispatcher,
    pathname,
    pushClosedStateToRouter,
    showModal,
    signup,
    search,
    trackEvent
  ]);

  const setClosedState = React.useCallback(() => {
    setShowModal(false);
    setSignup(false);
  }, []);

  const handleSignupModalClose = () => {
    setClosedState();
    pushClosedStateToRouter();
  };

  const isTeacherReferral = query?.teacherReferral === "true";
  const useTeacherFlow = query?.useTeacherFlow === "true";
  const isNotCloseable = query?.noClose === "true" || undefined;
  const shouldSkipLearners = query?.skipLearners === "true" || undefined;
  const isGiftCardSignup = query?.isGiftCardSignup === "true" || undefined;

  const activityUid =
    (helpers.isUuid(activitySlugOrUid) && activitySlugOrUid) || null;
  const activitySlugId =
    (!activityUid &&
      activitySlugOrUid &&
      Routes.getIdFromSlug(activitySlugOrUid)) ||
    null;
  const skipActivity = !activityUid && !activitySlugId;

  const leaderUid = (helpers.isUuid(uidOrLink) && uidOrLink) || null;
  const leaderLink = (!leaderUid && uidOrLink) || null;
  const skipLeader = !leaderUid && !leaderLink;

  // A querystring like ?usid=3&usid=3 can give us an array here instead of a string like we want
  // If this happens, just take the first value out of the array:
  const usid = query?.usid || null;
  const referrerSlugId = Array.isArray(usid) ? usid[0] : usid;
  const skipReferrer = !referrerSlugId;

  const authTrigger = query?.authTrigger;

  const variables = {
    skipActivity,
    activityUid,
    activitySlugId,
    referrerSlugId,
    skipReferrer,
    leaderUid,
    leaderLink,
    skipLeader
  };

  return (
    <Modal
      ariaLabel={t`Join Outschool`}
      open={showModal}
      onClose={handleSignupModalClose}
      fullBleed={false}
      hasCloseButton={!isNotCloseable}
      closeViaMaskClick={!isNotCloseable}
      closeViaEscButton={!isNotCloseable}
      iconButtonSx={{
        position: "relative",
        zIndex: 2
      }}
      sx={{
        maxWidth: 420,
        borderRadius: "large"
      }}
    >
      <Query
        query={signupModalQuery}
        skip={!query || !query.signup || isLoggedIn}
        variables={variables}
        fetchPolicy="no-cache"
      >
        {({
          loading,
          data: {
            activity = undefined,
            referrer = undefined,
            leader = undefined
          } = {}
        }) => {
          const leaderName = leader?.leader?.name;

          return (
            <Box data-test-id="signup-modal-content">
              {loading ? (
                <Loading data-test-id="signup-modal-loading" />
              ) : referrer ? (
                <SignupReferralModalContents
                  referrer={referrer}
                  onClickExplore={handleSignupModalClose}
                />
              ) : (
                <>
                  <SignupModalHeader
                    {...{
                      ...variables,
                      activity,
                      referrer,
                      leaderName,
                      authTrigger,
                      useTeacherFlow,
                      isTeacherReferral
                    }}
                  />
                  <SignupForm
                    promptLogin
                    onRedirect={setClosedState}
                    isTeacherFlow={isTeacherReferral || useTeacherFlow}
                    trackingUniqueId="signup-modal-signup-form"
                    signupParams={{
                      skipLearners: shouldSkipLearners,
                      isGiftCardSignup
                    }}
                  />
                </>
              )}
            </Box>
          );
        }}
      </Query>
    </Modal>
  );
}

export function SignupModalHeader({
  authTrigger,
  referrer,
  activity,
  isTeacherReferral,
  useTeacherFlow,
  leaderName
}) {
  if (authTrigger === AuthTrigger.JOIN_OUTSCHOOL) {
    return <JoinForFreeHeader isTeacherPath={useTeacherFlow} />;
  }
  if (authTrigger) {
    return (
      <AuthTriggerHeader
        authTrigger={authTrigger}
        leaderName={leaderName}
        referrer={referrer}
      />
    );
  }
  if (activity) {
    return <ActivityHeader activity={activity} referrer={referrer} />;
  }
  if (referrer) {
    return (
      <ReferrerHeader
        isTeacherReferral={isTeacherReferral}
        referrer={referrer}
      />
    );
  }
  return <DefaultHeader />;
}

type DefaultHeaderProps = {
  title?: string;
  subtitle?: string;
};

export function DefaultHeader({ title, subtitle }: DefaultHeaderProps) {
  const { t } = useTranslation("client\\components\\SignupModal");
  title = title ?? t`Join Outschool`;
  subtitle = subtitle ?? t`Amazing classes and teachers await!`;
  return (
    <div data-test-id="default-signup-header">
      <Box
        sx={{
          marginBottom: "1em",
          marginTop: "1em",
          textAlign: "center"
        }}
      >
        <Typography
          variant="h2"
          sx={{
            marginBottom: "0.5em"
          }}
        >
          {title}
        </Typography>
        {subtitle}
      </Box>
    </div>
  );
}

function AuthTriggerHeader({ authTrigger, leaderName, referrer }) {
  const isMobile = Screen.useIsMobile();
  const { t } = useTranslation("client\\components\\SignupModal");
  const getAuthReason = useGetAuthReason();
  const authReason = getAuthReason(authTrigger);
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  React.useEffect(() => {
    trackAdEvent("Visit sign up - parent", { authReason });
  }, [authReason, trackAdEvent]);

  return (
    <Container
      sx={{
        display: "grid",
        gap: "small",
        justifyItems: "center",
        marginBottom: "medium"
      }}
      data-test-id="signup-modal-auth-trigger-header"
    >
      {authTrigger === AuthTrigger.ENROLL ? (
        <Box
          sx={{
            width: "100%"
          }}
        >
          <Typography
            variant={isMobile ? "h3" : "h2"}
            sx={{
              textAlign: "center",
              paddingTop: "1em"
            }}
          >
            {t("Join to Enroll")}
          </Typography>
        </Box>
      ) : authTrigger === AuthTrigger.REDEEM_GIFT_CARD ||
        authTrigger === AuthTrigger.BUY_GIFT_CARD ? (
        <Box
          sx={{
            position: "relative"
          }}
        >
          <Box
            sx={{
              position: "absolute",
              left: "-32px",
              width: "calc(100% + 64px)",
              top: "-16px",
              height: "312px"
            }}
          >
            <Box
              sx={theme => ({
                position: "relative",
                width: "100%",
                height: "100%",
                borderRadius: "0.5em",

                [theme.breakpoints.up("md")]: {
                  borderRadius: "0.5em"
                },

                overflow: "hidden"
              })}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: "1072px",
                  height: "1147px",
                  bottom: "0px",
                  left: "calc(50% - 536px)",
                  borderRadius: "50%",
                  backgroundColor: "primary.200",
                  overflow: "hidden"
                }}
              />
            </Box>
          </Box>
          <Typography
            variant="h3"
            sx={{
              position: "relative",
              textAlign: "center",
              paddingTop: "1em"
            }}
          >
            {t("Join to {{authReason}}", { authReason })}
          </Typography>
          <Box
            sx={{
              position: "relative",
              textAlign: "center",
              width: "100%"
            }}
          >
            {t(
              "Outschool offers thousands of live online classes for kids ages 3–18."
            )}
          </Box>
          <Box
            sx={{
              position: "relative",
              margin: "auto",
              height: "225px",
              width: "225px"
            }}
          >
            <Image
              sx={{ position: "absolute", top: "18px" }}
              height={200}
              src={PerfectGiftForKids}
            />
          </Box>
        </Box>
      ) : referrer ? (
        <Box
          sx={{
            marginTop: "1em"
          }}
        >
          <ReferrerHeader referrer={referrer} />
        </Box>
      ) : (
        <Box>
          <Typography
            variant={isMobile ? "h3" : "h2"}
            sx={{
              textAlign: "center",
              paddingTop: "1em"
            }}
          >
            {t("Join to {{authReason}}", {
              authReason: leaderName
                ? authReason.replace("this teacher", leaderName)
                : authReason
            })}
          </Typography>
          <Box
            sx={{
              textAlign: "center",
              width: "100%"
            }}
          >
            {t("Free to join, pay as low as {{minimumPrice}} per class.", {
              minimumPrice: MIN_PRICE
            })}
          </Box>
        </Box>
      )}
    </Container>
  );
}

function JoinForFreeHeader({ isTeacherPath }) {
  const isMobile = Screen.useIsMobile();
  const { t } = useTranslation("client\\components\\SignupModal");
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const getAuthReason = useGetAuthReason();
  const authReason = getAuthReason(AuthTrigger.JOIN_OUTSCHOOL);
  React.useEffect(() => {
    if (isTeacherPath) {
      trackAdEvent("Visit sign up - teacher", { authReason });
    } else {
      trackAdEvent("Visit sign up - parent", { authReason });
    }
  }, [authReason, isTeacherPath, trackAdEvent]);

  return (
    <Box
      flex
      sx={{
        display: "grid",
        gap: "0.5em",
        justifyItems: "center",
        marginBottom: "2em"
      }}
      data-test-id="signup-modal-join-free-header"
    >
      <Typography
        variant={isMobile ? "h3" : "h2"}
        sx={{
          textAlign: "center",
          paddingTop: "1em"
        }}
      >
        {t("Join Outschool For Free")}
      </Typography>
      {!isTeacherPath && (
        <Box
          sx={{
            textAlign: "center",
            width: "100%"
          }}
        >
          {t(
            "Explore any interest with {{numClasses}}+ classes, pay as low as {{minimumPrice}} per class.",
            { numClasses: totalClassesString, minimumPrice: MIN_PRICE }
          )}
        </Box>
      )}
    </Box>
  );
}

function ReferrerHeader({ referrer, isTeacherReferral = false }) {
  const { t } = useTranslation("client\\components\\SignupModal");
  const referrerProfile = _.get(referrer, "leader.approved")
    ? referrer.leader
    : referrer.parent;
  const referrerName = _.trim(referrerProfile.name);
  const acceptanceText = isTeacherReferral
    ? t(
        "Accept {{referrerName}}'s invitation to get started teaching on Outschool.",
        { referrerName }
      )
    : t(
        "Accept {{referrerName}}'s invitation to get $20 off to try your first class.",
        { referrerName }
      );

  return (
    <Box
      data-test-id="signup-modal-referral-header"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between"
      }}
    >
      <UserHeadshotImage
        data-test-id="referrer-avatar"
        user={referrerProfile}
      />
      <Box
        data-test-id="referrer-message"
        sx={{
          marginBottom: "1em",
          marginTop: "1em",
          textAlign: "center"
        }}
      >
        {acceptanceText}
      </Box>
    </Box>
  );
}

type ActivityHeaderProps = {
  activity: Pick<SignupModalQuery["activity"], "title" | "details">;
  referrer?: SignupModalQuery["referrer"];
  title?: string;
  subtitle?: string;
};

export function ActivityHeader({
  activity,
  referrer,
  title,
  subtitle
}: ActivityHeaderProps) {
  const { t } = useTranslation("client\\components\\SignupModal");

  const { translate } = useMachineTranslation();

  title = title ?? t`Join Outschool`;
  let referrerName;
  if (referrer) {
    referrerName = _.get(referrer, "leader.approved")
      ? referrer.leader.name
      : referrer.parent.name;
  }
  subtitle =
    subtitle ||
    (referrerName
      ? t(
          "Accept {{referrerName}}'s invitation to get $20 off to try your first class",
          { referrerName }
        )
      : t("Discover amazing classes like:"));
  const imageUrl = FilestackUrls.imageUrl(activity.details.photo, {
    w: 375,
    h: 198,
    fit: "crop"
  });
  return (
    <div data-test-id="signup-modal-activity-header">
      <Box
        sx={{
          marginBottom: "1em",
          textAlign: "center"
        }}
      >
        <div>
          <Typography
            variant="h3"
            sx={{
              marginBottom: "0.5em"
            }}
          >
            {title}
          </Typography>
          <Box
            sx={{
              marginBottom: "1em"
            }}
          >
            {subtitle}
          </Box>
          <Image
            src={imageUrl}
            alt={t("User-provided cover image for this class")}
            sx={{
              width: "100%",
              boxSizing: "border-box",
              borderRadius: "4px",
              marginBottom: "1em"
            }}
          ></Image>
          <Box
            sx={{
              fontWeight: 500,
              color: "#808080",
              marginBottom: "1em"
            }}
          >
            {translate(activity.title)}
          </Box>
        </div>
      </Box>
    </div>
  );
}
